import {addMonths} from 'date-fns/addMonths'
import {endOfMonth} from 'date-fns/endOfMonth'
import {isWithinInterval} from 'date-fns/isWithinInterval'
import {parseISO} from 'date-fns/parseISO'
import {startOfMonth} from 'date-fns/startOfMonth'

import {AvailabilityHotelEntity} from '@vio/core/src/availability/types'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

export interface PricePredictionEntry {
  checkIn: string
  price: number
}

export const MONTHS_TO_SHOW = 2

/**
 * The priced entries that fall inside the months the calendar renders (the
 * month of `referenceDate` plus the next), sorted by date. Consumers that
 * re-expose the calendar (e.g. structured data) must use this so they cover
 * exactly the nights the user can see.
 */
export const getVisiblePricePredictionEntries = (
  prices: PricePredictionEntry[],
  referenceDate: Date
): PricePredictionEntry[] => {
  const interval = {
    start: startOfMonth(referenceDate),
    end: endOfMonth(addMonths(referenceDate, MONTHS_TO_SHOW - 1))
  }

  return prices
    .filter(
      ({checkIn, price}) =>
        price > 0 && isWithinInterval(parseISO(checkIn), interval)
    )
    .sort((a, b) => a.checkIn.localeCompare(b.checkIn))
}

/**
 * Adapts the price-prediction payload into an `AvailabilityHotelEntity` so the
 * existing coloring (`calculatePriceType`) and day cell (`ColorPriceDayInner`)
 * can be reused as-is.
 *
 * A `price` of 0 means "no data": those dates are omitted, so they render as a
 * bare day number and stay out of the month percentile used for coloring. Only
 * the fields the day cell reads are populated (`cheapestNightlyRate`, the offer
 * currency), so the offer is cast.
 */
export const pricesToAvailabilityEntity = (
  prices: PricePredictionEntry[],
  currency: string
): AvailabilityHotelEntity =>
  prices.reduce<AvailabilityHotelEntity>((entity, {checkIn, price}) => {
    if (price > 0) {
      entity[checkIn] = {
        cheapestNightlyRate: price,
        offers: [{currency} as SearchOffer]
      } as AvailabilityHotelEntity[string]
    }
    return entity
  }, {})
